import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BadgeIcon from '@mui/icons-material/Badge';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import KeyIcon from '@mui/icons-material/Key';
import LockIcon from '@mui/icons-material/Lock';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { getUiLanguage, tr, uiLocale } from '../lib/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { AppNotification, NotificationsPage, SubscriptionSummary } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button } from './button';

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const TYPE_ICON: Record<string, { icon: ReactNode; color: string }> = {
  WELCOME: { icon: <CelebrationIcon fontSize="small" />, color: '#0B57D0' },
  REQUEST_ASSIGNED: { icon: <AssignmentIndIcon fontSize="small" />, color: '#188038' },
  REQUESTS_ASSIGNED: { icon: <AssignmentIndIcon fontSize="small" />, color: '#188038' },
  NEW_PUBLIC_REQUEST: { icon: <FiberNewIcon fontSize="small" />, color: '#E37400' },
  NEW_PUBLIC_PROPERTY: { icon: <FiberNewIcon fontSize="small" />, color: '#E37400' },
  REQUEST_MATCHES: { icon: <JoinInnerIcon fontSize="small" />, color: '#0B57D0' },
  PROPERTY_MATCHES: { icon: <JoinInnerIcon fontSize="small" />, color: '#0B57D0' },
  NOTE_ADDED: { icon: <ChatBubbleOutlineIcon fontSize="small" />, color: '#5F6368' },
  REQUEST_STATUS: { icon: <SwapHorizIcon fontSize="small" />, color: '#5F6368' },
  PROPERTY_STATUS: { icon: <SwapHorizIcon fontSize="small" />, color: '#5F6368' },
  ROLE_CHANGED: { icon: <BadgeIcon fontSize="small" />, color: '#0B57D0' },
  PERMISSIONS_CHANGED: { icon: <ManageAccountsIcon fontSize="small" />, color: '#0B57D0' },
  PASSWORD_RESET: { icon: <KeyIcon fontSize="small" />, color: '#D93025' },
  ACCOUNT_ENABLED: { icon: <HowToRegIcon fontSize="small" />, color: '#188038' },
  OWNERSHIP: { icon: <EmojiEventsIcon fontSize="small" />, color: '#E37400' },
  SUBSCRIPTION_RENEWED: { icon: <CheckCircleIcon fontSize="small" />, color: '#188038' },
  SUBSCRIPTION_UPDATED: { icon: <WorkspacePremiumIcon fontSize="small" />, color: '#0B57D0' },
  SUBSCRIPTION_EXPIRING: { icon: <WarningAmberIcon fontSize="small" />, color: '#E37400' },
  SUBSCRIPTION_GRACE: { icon: <WarningAmberIcon fontSize="small" />, color: '#D93025' },
  SUBSCRIPTION_LOCKED: { icon: <LockIcon fontSize="small" />, color: '#D93025' },
};
const iconOf = (t: string) => TYPE_ICON[t] ?? { icon: <NotificationsIcon fontSize="small" />, color: '#5F6368' };

/** Notification text in the reader's interface language (falls back to Arabic for old rows). */
export const nTitle = (n: AppNotification) => (getUiLanguage() === 'en' && n.title_en ? n.title_en : n.title);
export const nBody = (n: AppNotification) => (getUiLanguage() === 'en' && n.title_en ? n.body_en ?? null : n.body);

export function timeAgo(d: string): string {
  const s = Math.max(0, (Date.now() - new Date(d).getTime()) / 1000);
  if (getUiLanguage() === 'en') {
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    if (s < 60) return rtf.format(0, 'second');
    const m = Math.floor(s / 60);
    if (m < 60) return rtf.format(-m, 'minute');
    const h = Math.floor(m / 60);
    if (h < 24) return rtf.format(-h, 'hour');
    const days = Math.floor(h / 24);
    if (days < 30) return rtf.format(-days, 'day');
    return new Date(d).toLocaleDateString('en-US', { dateStyle: 'medium' });
  }
  if (s < 60) return 'الآن';
  const m = Math.floor(s / 60);
  if (m < 60) return m === 1 ? 'منذ دقيقة' : m === 2 ? 'منذ دقيقتين' : `منذ ${m} دقائق`;
  const h = Math.floor(m / 60);
  if (h < 24) return h === 1 ? 'منذ ساعة' : h === 2 ? 'منذ ساعتين' : `منذ ${h} ساعات`;
  const days = Math.floor(h / 24);
  if (days === 1) return 'أمس';
  if (days < 30) return `منذ ${days} ${days <= 10 ? 'أيام' : 'يوماً'}`;
  return new Date(d).toLocaleDateString('ar-JO-u-nu-latn', { dateStyle: 'medium' });
}

const fmtLongDay = (d: string | null) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(uiLocale(), { dateStyle: 'long' }) : '—');
const fmtDuration = (n: number | null, u: 'DAY' | 'MONTH' | null) =>
  !n || !u ? '' : u === 'MONTH' ? (n === 12 ? tr('سنة', '1 year') : n === 1 ? tr('شهر', '1 month') : tr(`${n} أشهر`, `${n} months`)) : n === 1 ? tr('يوم', '1 day') : tr(`${n} يوماً`, `${n} days`);
const fmtPrice = (p: string | null, c: string | null) => (p === null ? '' : Number(p) === 0 ? tr('مجانية', 'Free') : `${Number(p).toLocaleString(uiLocale())} ${c === 'JOD' || !c ? tr('د.أ', 'JOD') : c}`);

/** Mutations shared by the bell, the page and the welcome dialog. */
export function useNotificationActions() {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const markRead = useMutation({ mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'POST', body: {} }), onSuccess: refresh });
  const markAll = useMutation({ mutationFn: () => api('/notifications/read-all', { method: 'POST', body: {} }), onSuccess: refresh });
  return { markRead, markAll };
}

export function NotificationItem({ n, onOpen, dense }: { n: AppNotification; onOpen: (n: AppNotification) => void; dense?: boolean }) {
  const ic = iconOf(n.type);
  return (
    <ListItemButton onClick={() => onOpen(n)} alignItems="flex-start" sx={{ py: dense ? 1 : 1.5, bgcolor: n.read_at ? undefined : 'action.hover', borderRadius: 1, mb: 0.5 }}>
      <ListItemAvatar sx={{ minWidth: 48 }}>
        <Avatar sx={{ width: 34, height: 34, bgcolor: `${ic.color}1A`, color: ic.color }}>{ic.icon}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={n.read_at ? 500 : 700} sx={{ flex: 1 }}>{nTitle(n)}</Typography>
            {!n.read_at && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />}
          </Stack>
        }
        secondary={
          <>
            {nBody(n) && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: dense ? 2 : 6, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-line' }}>
                {nBody(n)}
              </Typography>
            )}
            <Typography component="span" variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25 }}>{timeAgo(n.created_at)}</Typography>
          </>
        }
      />
    </ListItemButton>
  );
}

// ---------------------------------------------------------------------------
// Bell (AppBar)
// ---------------------------------------------------------------------------
export function NotificationsBell() {
  const nav = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { markRead, markAll } = useNotificationActions();
  const count = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api<{ unreadCount: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const list = useQuery({
    queryKey: ['notifications', 'latest'],
    queryFn: () => api<NotificationsPage>('/notifications', { query: { limit: 15 } }),
    enabled: !!anchor,
  });
  const unread = count.data?.unreadCount ?? 0;

  const open = (n: AppNotification) => {
    if (!n.read_at) markRead.mutate(n.id);
    setAnchor(null);
    if (n.link) nav(n.link);
    else nav('/notifications');
  };

  return (
    <>
      <Tooltip title={tr('الإشعارات', 'Notifications')}>
        <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ ml: 0.5 }}>
          <Badge badgeContent={unread} color="error" max={99}>
            {unread ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 24px)', maxHeight: 520, display: 'flex', flexDirection: 'column' } } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5 }}>
          <Typography fontWeight={700} sx={{ flex: 1 }}>{tr('الإشعارات', 'Notifications')} {unread ? <Chip size="small" label={unread} color="error" sx={{ marginInlineStart: 1 }} /> : null}</Typography>
          <Button size="small" startIcon={<DoneAllIcon />} disabled={!unread || markAll.isPending} onClick={() => markAll.mutate()}>{tr('تعليم الكل كمقروء', 'Mark all as read')}</Button>
        </Stack>
        <Divider />
        <Box sx={{ overflowY: 'auto', flex: 1, px: 1, py: 1 }}>
          {list.isLoading ? (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>{tr('جارٍ التحميل…', 'Loading…')}</Typography>
          ) : (list.data?.items ?? []).length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ p: 4, color: 'text.secondary' }}>
              <NotificationsNoneIcon />
              <Typography variant="body2">{tr('لا توجد إشعارات بعد', 'No notifications yet')}</Typography>
            </Stack>
          ) : (
            <List disablePadding>
              {list.data!.items.map((n) => <NotificationItem key={n.id} n={n} onOpen={open} dense />)}
            </List>
          )}
        </Box>
        <Divider />
        <Button sx={{ m: 1 }} onClick={() => { setAnchor(null); nav('/notifications'); }}>{tr('عرض كل الإشعارات', 'View all notifications')}</Button>
      </Popover>
    </>
  );
}

// ---------------------------------------------------------------------------
// Subscription details (welcome dialog + "my subscription")
// ---------------------------------------------------------------------------
const ACCESS_TEXT: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  ACTIVE: { label: tr('فعّال', 'Active'), color: 'success' },
  EXPIRING: { label: tr('ينتهي قريباً', 'Expiring soon'), color: 'warning' },
  GRACE: { label: tr('منتهٍ — مهلة سماح', 'Expired — grace period'), color: 'error' },
  LOCKED: { label: tr('منتهٍ — للعرض فقط', 'Expired — view only'), color: 'error' },
  NONE: { label: tr('غير محدد', 'Not specified'), color: 'default' },
};

export function SubscriptionDetails({ s }: { s: SubscriptionSummary }) {
  const a = ACCESS_TEXT[s.access] ?? ACCESS_TEXT.NONE!;
  const rows: [string, ReactNode][] = [
    [tr('الباقة', 'Plan'), s.planName ? `${(getUiLanguage() === 'en' && s.planNameEn) || s.planName}${s.price !== null ? ` — ${fmtPrice(s.price, s.currency)}` : ''}${s.durationCount ? ` / ${fmtDuration(s.durationCount, s.durationUnit)}` : ''}` : '—'],
    [tr('ينتهي في', 'Ends on'), fmtLongDay(s.endsAt)],
    [tr('المتبقي', 'Remaining'), s.daysLeft === null ? '—' : s.daysLeft >= 0 ? tr(`${s.daysLeft} يوم`, `${s.daysLeft} days`) : tr(`انتهى منذ ${-s.daysLeft} يوم`, `Expired ${-s.daysLeft} days ago`)],
    [tr('الحالة', 'Status'), <Chip key="st" size="small" color={a.color} label={a.label} />],
  ];
  if (s.access === 'GRACE' && s.graceEndsAt) rows.push([tr('آخر يوم قبل الإيقاف', 'Last day before lock'), fmtLongDay(s.graceEndsAt)]);
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <WorkspacePremiumIcon color="primary" />
        <Typography fontWeight={700}>{tr('اشتراك', 'Subscription for')} {(getUiLanguage() === 'en' && s.organizationNameEn) || s.organizationName}</Typography>
      </Stack>
      <Stack spacing={1}>
        {rows.map(([k, v]) => (
          <Stack key={k} direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">{k}</Typography>
            <Typography variant="body2" fontWeight={600} component="div">{v}</Typography>
          </Stack>
        ))}
      </Stack>
      {(s.access === 'EXPIRING' || s.access === 'GRACE' || s.access === 'LOCKED') && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 1.5 }}>{tr('تواصلوا معنا لتجديد الاشتراك.', 'Contact us to renew the subscription.')}</Typography>
      )}
    </Box>
  );
}

/** "اشتراكي" from the user menu (owners / managers). */
export function MySubscriptionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const q = useQuery({ queryKey: ['subscription'], queryFn: () => api<SubscriptionSummary>('/subscription'), enabled: open });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{tr('الاشتراك', 'Subscription')}</DialogTitle>
      <DialogContent>{q.data ? <SubscriptionDetails s={q.data} /> : <Typography color="text.secondary">{tr('جارٍ التحميل…', 'Loading…')}</Typography>}</DialogContent>
      <DialogActions><Button onClick={onClose}>{tr('إغلاق', 'Close')}</Button></DialogActions>
    </Dialog>
  );
}

/** Opens by itself on the first login (unread WELCOME notification): greeting + subscription + how notifications work. */
export function WelcomeDialog() {
  const { me } = useAuth();
  const { markRead } = useNotificationActions();
  const [dismissed, setDismissed] = useState(false);
  const q = useQuery({
    queryKey: ['notifications', 'welcome'],
    queryFn: () => api<NotificationsPage>('/notifications', { query: { unread: 'true', limit: 50 } }),
    enabled: !!me,
    staleTime: Infinity,
  });
  const welcome = q.data?.items.find((n) => n.type === 'WELCOME');
  useEffect(() => setDismissed(false), [welcome?.id]);
  if (!welcome || dismissed) return null;

  const close = () => {
    setDismissed(true);
    markRead.mutate(welcome.id);
  };
  const sub = welcome.data.subscription;
  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main' }}><CelebrationIcon /></Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>{nTitle(welcome)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {me?.user.full_name}
              {(getUiLanguage() === 'en' ? welcome.data.roleEn ?? welcome.data.role : welcome.data.role)
                ? ` · ${String(getUiLanguage() === 'en' ? welcome.data.roleEn ?? welcome.data.role : welcome.data.role)}`
                : ''}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>{tr('يسعدنا انضمامك. هذه لوحة شركتك لإدارة العقارات والطلبات.', 'Welcome! This is your organization dashboard for managing properties and requests.')}</Typography>
          {sub && <SubscriptionDetails s={sub} />}
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <NotificationsIcon color="primary" fontSize="small" />
              <Typography fontWeight={700}>{tr('الإشعارات', 'Notifications')}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {tr('الجرس في أعلى الصفحة يخبرك فوراً بما يهمك: الطلبات المعيّنة لك، الطلبات الجديدة من الرابط العام، الملاحظات وتغييرات الحالة على أعمالك', 'The bell at the top keeps you informed about assigned requests, new public-link entries, notes, and status changes')}
              {sub ? tr('، وتذكيرات تجديد الاشتراك قبل انتهائه', ', plus subscription renewal reminders') : ''}.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={close}>{tr('لنبدأ', 'Get started')}</Button>
      </DialogActions>
    </Dialog>
  );
}
